import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'

import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { locale } from 'modules/marketplace_modules/components/utils/locales'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'

import { AvailabilityFilter } from '../components/List/ModuleListSearchProvider'
import useActiveSubscriptionOverload from './useActiveSubscriptionOverload'

const useModulesList = () => {
  const { moduleAvailabilities } = useModuleAvailabilities()
  const session = useCurrentSession()
  const datoFormatLocale = locale[session.access.locale]
  const countryCodeForCompany = countryCode(session.company.legalEntities[0]?.country || '')
  const { data: modulesData, loading } = useGetDatoModules({
    locale: datoFormatLocale,
    countryCode: countryCodeForCompany,
  })
  const { activeSubscriptionOverload } = useActiveSubscriptionOverload([session.company.id])

  const isModuleNotAvailable = (chargebeeModuleName: string) => {
    const availableModules = moduleAvailabilities.filter((obj) => {
      const modulesArray = chargebeeModuleName.split(',')
      return !obj.available && modulesArray.includes(obj.id)
    })

    return !!availableModules.length
  }

  const allModules = modulesData

  const filteredModules = (
    availabilityFilter: AvailabilityFilter | undefined,
    searchValue: string,
    categoryFilter: string
  ) => {
    const lowerCaseSearchValue = searchValue.toLowerCase()
    const onTrial = activeSubscriptionOverload?.addonIds

    const modules = allModules
      .filter((child) => !searchValue || child.name?.toLowerCase().includes(lowerCaseSearchValue))
      .filter((child) => !categoryFilter || child.category.id === categoryFilter)

    const comingSoonModules = modules.filter((module) => module.statusBadge === 'coming soon')

    const onTrialModules = modules.filter((module) => {
      const moduleIds = module.chargebeeModuleId?.split(',')
      return moduleIds?.some((id) => onTrial?.includes(id))
    })

    if (availabilityFilter === AvailabilityFilter.comingSoon) return comingSoonModules
    if (availabilityFilter === AvailabilityFilter.onTrial) return onTrialModules

    const nonTrialModules = modules.filter(
      (module) => !onTrialModules.includes(module) && !comingSoonModules.includes(module)
    )

    if (availabilityFilter === undefined) {
      const nonTrialModulesAvailable = nonTrialModules.filter(
        (module) => !isModuleNotAvailable(module?.chargebeeModuleName || '')
      )

      const nonTrialModulesNotAvailable = nonTrialModules.filter((module) =>
        isModuleNotAvailable(module?.chargebeeModuleName || '')
      )

      return [...nonTrialModulesNotAvailable, ...onTrialModules, ...nonTrialModulesAvailable]
    }

    return nonTrialModules.filter((child) => {
      const isAvailable = availabilityFilter === AvailabilityFilter.available
      const availability = moduleAvailabilities.find((obj) => obj.id === child.chargebeeModuleName)
      return availability?.available === isAvailable
    })
  }

  const popularModules = () => {
    return allModules.filter((child) => isModuleNotAvailable(child.chargebeeModuleName || ''))
  }

  const getCategoriesNames = () => {
    const categoriesSet = new Set(allModules.map((module) => module.category.title))
    const categories = Array.from(categoriesSet).map((title) => ({
      label: title,
      value: allModules.find((module) => module.category.title === title)?.category.id,
    }))

    return categories
  }

  return { loading, popularModules, allModules, filteredModules, getCategoriesNames }
}

export default useModulesList
